import { UseMutateFunction } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  HostingOnboardingDataUpdate200,
  HostingOnboardingDataUpdateBody,
  SiteUpdate200,
  SiteUpdate400,
  SiteUpdateBody,
} from '@newfold/huapi-js/src/index.schemas';

import useAccount from '~/hooks/useAccount';
import useDataCenter from '~/hooks/useDataCenter';

import { AddSiteFormDataTypes } from '..';
import { useAddSiteContext } from '../context/AddSiteContext';
import BtnGroup from './MultiStepCard/BtnGroup';
import CardHeader from './MultiStepCard/CardHeader';

type ChooseTitlePropOptions = {
  formData?: AddSiteFormDataTypes;
  setFormData?: (state: AddSiteFormDataTypes | any) => void;
  changeLoading?: boolean;
  setChangeLoading?: (state: boolean) => void;
  hostingOnboardingDataUpdate?: UseMutateFunction<
    AxiosResponse<HostingOnboardingDataUpdate200, any>,
    AxiosError<unknown, any>,
    {
      hostingId: string;
      data: HostingOnboardingDataUpdateBody;
    },
    unknown
  >;
  siteUpdate?: UseMutateFunction<
    AxiosResponse<SiteUpdate200, any>,
    AxiosError<SiteUpdate400, any>,
    {
      siteId: number;
      data: SiteUpdateBody;
    },
    unknown
  >;
};

const ChooseTitle = ({
  formData = {},
  setFormData = () => {},
  changeLoading = false,
  setChangeLoading = () => {},
  hostingOnboardingDataUpdate = () => {},
  siteUpdate = () => {},
}: ChooseTitlePropOptions) => {
  const { t } = useTranslation('addSites');
  const { id: hostingId } = useAccount();
  const navigate = useNavigate();
  const { siteId } = useParams();
  const { optionSelected } = useAddSiteContext();
  const { dCName, dCType, isPending, isError } = useDataCenter();

  const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiFilledInput-root': {
      border: '1px solid',
      paddingTop: '3px',
      borderRadius: '8px',
    },
    '& .MuiFilledInput-input': {
      fontSize: '16px',
      paddingTop: '3px',
      paddingBottom: '8px',
      fontWeight: 'bold',
    },
    '& .MuiFilledInput-input.Mui-disabled': {
      color: theme.palette.text.secondary,
      WebkitTextFillColor: theme.palette.text.secondary,
    },
  }));

  const handleSkip = () => {
    // @ts-expect-error
    setFormData({ ...formData, siteName: '' }, addSite());
  };

  const setupSite = () => {
    setChangeLoading(true);
    hostingOnboardingDataUpdate({
      hostingId,
      data: { completed: true },
    });
    siteUpdate({
      siteId: Number(siteId),
      data: {
        title: formData.siteName,
        domain: formData.domain,
        path: formData.path,
      },
    });
  };

  const addSite = () => {
    navigate('../connect');
  };

  const isSiteForSetup = !!siteId;

  return (
    <Stack spacing={2}>
      <CardHeader headerTitle={t('chooseSiteTitle')} />
      <Typography>{t('websiteNameDescription')}</Typography>
      <Stack spacing={4}>
        <Grid container flexDirection="column" rowSpacing={2}>
          <Grid item xs={12} md={10}>
            <TextField
              fullWidth
              id="outlined-basic"
              label="Site Title"
              data-testid="site-title-input"
              variant="outlined"
              value={formData.siteName}
              onChange={(e) => {
                e.preventDefault();
                setFormData({ ...formData, siteName: e.target.value });
              }}
            />
          </Grid>
          {dCType === 'oci' && (
            <Grid item xs={12} md={10}>
              <Typography variant="h4">{t('dcTitle')}</Typography>
              <Stack spacing={1} mt={2}>
                <StyledTextField
                  fullWidth
                  value={dCName || ''}
                  id="outlined-basic"
                  data-testid="dc-location-input"
                  variant="filled"
                  disabled
                  InputProps={{
                    disableUnderline: true,
                  }}
                  sx={{ marginTop: 2 }}
                />
                <Typography variant="h5">{t('dcLocationInfo')}</Typography>
              </Stack>
            </Grid>
          )}
        </Grid>

        <BtnGroup
          type={formData.subtype || optionSelected}
          hasSkipBtn={!isSiteForSetup}
          loading={changeLoading}
          disabled={formData.siteName === '' || isPending || isError}
          onSkipHandler={handleSkip}
          onClickHandler={isSiteForSetup ? setupSite : addSite}
          dataTestId={
            isSiteForSetup ? 'setup-site-flow-btn' : 'add-site-flow-btn'
          }
        />
      </Stack>
    </Stack>
  );
};

export default ChooseTitle;
